import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp } from "@/lib/icons";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RowActions } from "@/components/app/patterns/row-actions";
import { RecordCard } from "@/components/app/lists/record-card";
import { EmptyRows } from "@/components/app/lists/empty-rows";
import { RecordRow } from "@/components/app/lists/record-row";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { DeviceFormDialog, type DeviceDraft } from "./-components/kiosk/device-form-dialog";
import { RowsSkeleton } from "@/routes/_authenticated/_administration/settings/-components/rows-skeleton";

export const Route = createFileRoute("/_authenticated/_administration/settings/assistive-devices")({
  beforeLoad: ({ context }) => guardRoute(context.me, "assistive-devices"),
  component: AssistiveDevicesSettings,
});

const fail = (error: Error) => toast.error(errorWords(error));

/** Swaps a row with its neighbour, which is what an arrow means. */
const moved = (ids: string[], index: number, by: -1 | 1) => {
  const next = [...ids];
  const target = index + by;
  [next[index], next[target]] = [next[target]!, next[index]!];
  return next;
};

/** The order is the chart's display order. The code is what charts already stored, so it is fixed
 * once created; only the label is editable. */
function AssistiveDevicesSettings() {
  // One list for every organization, so only the platform team edits it.
  const canManage = useCan("platform:manage");
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<DeviceDraft | null>(null);
  const [removing, setRemoving] = useState<{ id: string; label: string } | null>(null);

  const options = useQuery(
    orpc.administration.catalogs.assistiveDevices.list.queryOptions({
      input: { includeInactive: true },
    }),
  );
  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: orpc.administration.catalogs.assistiveDevices.key(),
    });

  const update = useMutation(
    orpc.administration.catalogs.assistiveDevices.update.mutationOptions({
      onSuccess: invalidate,
      onError: fail,
    }),
  );
  const reorder = useMutation(
    orpc.administration.catalogs.assistiveDevices.reorder.mutationOptions({
      onSuccess: invalidate,
      onError: fail,
    }),
  );
  const remove = useMutation(
    orpc.administration.catalogs.assistiveDevices.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Device deleted");
        setRemoving(null);
        await invalidate();
      },
      onError: fail,
    }),
  );

  const rows = options.data ?? [];
  const ids = rows.map((row) => row.id);

  return (
    <>
      <PageHeader
        title="Assistive devices"
        meta="The walkers, wheelchairs and hearing aids staff can pick when they record what a resident uses, in the order the chart shows them."
      />

      <RecordCard
        title="Devices"
        count={options.data ? rows.length : undefined}
        canWrite={canManage}
        addLabel="Add device"
        onAdd={() => setEditing({ id: null, code: "", label: "" })}
      >
        {options.isPending ? (
          <RowsSkeleton />
        ) : options.isError ? (
          <ReadFailed
            message="The assistive devices could not be loaded."
            error={options.error}
            retrying={options.isFetching}
            onRetry={() => void options.refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyRows text="No devices yet. Add one so staff can pick it on a chart, like Rollator." />
        ) : (
          <ul className="divide-y">
            {rows.map((option, index) => (
              <RecordRow
                key={option.id}
                title={
                  <span className="flex flex-wrap items-center gap-2">
                    {option.label}
                    {option.isSystem ? <Badge variant="outline">Standard</Badge> : null}
                    {option.isActive ? null : <Badge variant="secondary">Off</Badge>}
                  </span>
                }
                meta={option.code}
                actions={
                  canManage ? (
                    <>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Move ${option.label} up`}
                        disabled={index === 0 || reorder.isPending}
                        onClick={() => reorder.mutate({ orderedIds: moved(ids, index, -1) })}
                      >
                        <ArrowUp className="size-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Move ${option.label} down`}
                        disabled={index === rows.length - 1 || reorder.isPending}
                        onClick={() => reorder.mutate({ orderedIds: moved(ids, index, 1) })}
                      >
                        <ArrowDown className="size-4" />
                      </Button>
                      <RowActions
                        modal={false}
                        label={option.label}
                        primary={{
                          label: "Rename",
                          onSelect: () =>
                            setEditing({ id: option.id, code: option.code, label: option.label }),
                        }}
                        items={[
                          {
                            label: option.isActive ? "Switch off" : "Switch on",
                            disabled: update.isPending,
                            onSelect: () =>
                              update.mutate({ optionId: option.id, isActive: !option.isActive }),
                          },
                          !option.isSystem && {
                            label: "Delete device",
                            destructive: true,
                            onSelect: () => setRemoving({ id: option.id, label: option.label }),
                          },
                        ]}
                      />
                    </>
                  ) : null
                }
              />
            ))}
          </ul>
        )}
      </RecordCard>

      {editing ? (
        <DeviceFormDialog draft={editing} onClose={() => setEditing(null)} onSaved={invalidate} />
      ) : null}

      {removing ? (
        <RemoveConfirm
          title={`Delete “${removing.label}”?`}
          confirmLabel="Delete device"
          description="Charts that already name this device keep what they stored, but it stops being offered for good. Switching it off hides it too and can be undone."
          onConfirm={() => remove.mutate({ optionId: removing.id })}
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </>
  );
}
